import type { Produto } from 'config/produto-env';
import type {
  CategoriaPremio,
  InstanciaEtapa1,
  Papel,
  PapelTipo,
  Pessoa,
  Programa,
  TipoUnidade,
  Unidade,
} from 'types/pessoa';

/**
 * Regras do regulamento 2027 que dependem da pessoa.
 * Funções puras: aceitam `null`/`undefined` e nunca lançam exceção.
 * Inelegibilidade e equipe organizadora ainda não têm dados em `player_fields__c`.
 */

type PessoaOpcional = Pessoa | null | undefined;

export type CategoriaPreInscricao = 'CONEXAO' | 'PILAR' | 'ELBB';

export type MotivoInelegibilidade = 'GRUPO_INELEGIVEL' | 'EGRESSO_SEM_ADESAO';

export type MotivoSemPreInscricao = 'INELEGIVEL' | 'EQUIPE_ORGANIZADORA' | 'SEM_PROGRAMA';

/** Programa da base que libera cada categoria de pré-inscrição, por produto. */
const PROGRAMAS_POR_PRODUTO: Record<Produto, Partial<Record<Programa, CategoriaPreInscricao>>> = {
  PREMIO_BB: { PREMIO_CONEXAO: 'CONEXAO', JOGA_JUNTO_PREDEFINIDO: 'PILAR' },
  PREMIO_ELBB: { ELBB: 'ELBB' },
};

/** UORs das equipes organizadoras do Prêmio (7.1.11.3). */
const UORS_EQUIPE_ORGANIZADORA = [511134, 532323];

const SITUACAO_FORCA_TRABALHO_REAL = 100;

const MATRICULA_REGEX = /^F[0-9]{7}$/;

export function getCategoriasPreInscricao(
  pessoa: PessoaOpcional,
  produto: Produto
): CategoriaPreInscricao[] {
  const mapa = PROGRAMAS_POR_PRODUTO[produto];
  return (pessoa?.programas ?? [])
    .map((programa) => mapa[programa])
    .filter((categoria): categoria is CategoriaPreInscricao => Boolean(categoria));
}

/** Motivo de inelegibilidade ao Prêmio (7.1.3.2), ou `null` se a pessoa é elegível. */
export function getMotivoInelegibilidade(pessoa: PessoaOpcional): MotivoInelegibilidade | null {
  if (pessoa?.funcao?.grupo) return 'GRUPO_INELEGIVEL';
  if (pessoa?.egresso_sem_adesao) return 'EGRESSO_SEM_ADESAO';
  return null;
}

export function isEquipeOrganizadora(pessoa: PessoaOpcional): boolean {
  const uor = pessoa?.lotacao?.uor;
  return uor != null && UORS_EQUIPE_ORGANIZADORA.includes(uor);
}

/** Primeiro motivo que impede a pré-inscrição no produto, ou `null` se há ao menos uma categoria liberada. */
export function getMotivoSemPreInscricao(
  pessoa: PessoaOpcional,
  produto: Produto
): MotivoSemPreInscricao | null {
  if (getMotivoInelegibilidade(pessoa)) return 'INELEGIVEL';
  if (isEquipeOrganizadora(pessoa)) return 'EQUIPE_ORGANIZADORA';
  if (getCategoriasPreInscricao(pessoa, produto).length === 0) return 'SEM_PROGRAMA';
  return null;
}

/** Categorias de pré-inscrição do produto, independentemente da pessoa. */
export function getCategoriasDoProduto(produto: Produto): CategoriaPreInscricao[] {
  return Object.values(PROGRAMAS_POR_PRODUTO[produto]);
}

/** Categorias em que a pessoa pode cadastrar pré-inscrição no produto. */
export function getCategoriasPermitidas(
  pessoa: PessoaOpcional,
  produto: Produto
): CategoriaPreInscricao[] {
  if (getMotivoSemPreInscricao(pessoa, produto)) return [];
  return getCategoriasPreInscricao(pessoa, produto);
}

export function podeCadastrarPreInscricao(
  pessoa: PessoaOpcional,
  produto: Produto,
  categoria: CategoriaPreInscricao
): boolean {
  return getCategoriasPermitidas(pessoa, produto).includes(categoria);
}

/** Unidade subordinante: UT, senão UE (7.1.7.4). */
export function getUnidadeSubordinante(pessoa: PessoaOpcional, categoria?: CategoriaPreInscricao): Unidade | null {
  const lotacao = pessoa?.lotacao;
  // Pilar de Sustentação aprova na diretoria (UE); as demais, na superintendência (UT).
  return (categoria === 'PILAR' ? lotacao?.ue ?? lotacao?.ut : lotacao?.ut ?? lotacao?.ue) ?? null;
}

/** Unidades do "Alterar": sem tipo definido para a categoria, mostra todas. */
export function filtrarUnidadesPorTipo(unidades: Unidade[], tipo: TipoUnidade | null): Unidade[] {
  return tipo ? unidades.filter((unidade) => unidade.tipo === tipo) : unidades;
}

export interface FiltroPapel {
  unidade?: number;
  instancia?: InstanciaEtapa1;
  categoria?: CategoriaPremio;
}

/** Todos os itens do papel; a mesma pessoa pode ter o papel em mais de uma unidade. */
export function getPapeis(pessoa: PessoaOpcional, papel: PapelTipo, filtro: FiltroPapel = {}): Papel[] {
  return (pessoa?.papeis ?? []).filter(
    (item) =>
      item.papel === papel &&
      (filtro.unidade === undefined || item.unidade === filtro.unidade) &&
      (filtro.instancia === undefined || item.instancia === filtro.instancia) &&
      (filtro.categoria === undefined || item.categorias.includes(filtro.categoria))
  );
}

export function hasPapel(pessoa: PessoaOpcional, papel: PapelTipo, filtro?: FiltroPapel): boolean {
  return getPapeis(pessoa, papel, filtro).length > 0;
}

export function isExterno(pessoa: PessoaOpcional): boolean {
  return pessoa?.tipo === 'EXTERNO';
}

export function isCedidoElbb(pessoa: PessoaOpcional): boolean {
  return pessoa?.cessao_elbb != null;
}

export function isForcaTrabalhoReal(pessoa: PessoaOpcional): boolean {
  return pessoa?.situacao?.codigo === SITUACAO_FORCA_TRABALHO_REAL;
}

export function isMatricula(valor: unknown): valor is string {
  return typeof valor === 'string' && MATRICULA_REGEX.test(valor);
}

export function getNomeExibicao(pessoa: PessoaOpcional): string | null {
  return pessoa?.nome_guerra ?? pessoa?.nome ?? null;
}
